import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { Calendar, Church, Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns'
import toast from 'react-hot-toast'

interface SaintInfo {
  date: string
  saint_name: string
  description: string
  is_feast_day: boolean
}

interface MonthSaints {
  month: number
  year: number
  saints: Array<{
    date: string
    day: number
    saint_name: string
    description: string
    is_feast_day: boolean
  }>
}

export const SaintsCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [todaySaint, setTodaySaint] = useState<SaintInfo | null>(null)
  const [monthSaints, setMonthSaints] = useState<MonthSaints | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedSaint, setSelectedSaint] = useState<SaintInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEnabled, setIsEnabled] = useState(true)

  useEffect(() => {
    loadTodaySaint()
    loadMonthSaints(currentDate)
  }, [])

  useEffect(() => {
    loadMonthSaints(currentDate)
  }, [currentDate])

  const loadTodaySaint = async () => {
    try {
      const data = await apiClient.get('/api/saints/saint-of-day')
      setTodaySaint(data)
    } catch (error) {
      console.error('Failed to load saint of the day:', error)
    }
  }

  const loadMonthSaints = async (date: Date) => {
    setLoading(true)
    try {
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      const data = await apiClient.get(`/api/saints/saints/month?month=${month}&year=${year}`)
      setMonthSaints(data)
    } catch (error) {
      console.error('Failed to load saints for month:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSaintForDate = async (date: Date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd')
      const data = await apiClient.get(`/api/saints/saint-of-day?specific_date=${dateStr}`)
      setSelectedSaint(data)
      setSelectedDate(date)
    } catch (error) {
      console.error('Failed to load saint for date:', error)
    }
  }

  const getSaintForDay = (day: number) => {
    if (!monthSaints) return null
    return monthSaints.saints.find(s => s.day === day)
  }

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
  }

  const handleToggleFeature = async () => {
    try {
      await apiClient.put('/api/settings/saints_calendar_enabled', { value: !isEnabled })
      setIsEnabled(!isEnabled)
      toast.success(`Saints calendar ${!isEnabled ? 'enabled' : 'disabled'}`)
    } catch (error: any) {
      console.error('Failed to update setting:', error)
      toast.error(error.response?.data?.error || 'Failed to update setting')
    }
  }

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

  return (
    <div className="saints-calendar">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Saints Calendar</h2>
            <p className="text-gray-600">Daily saints and feast days</p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={handleToggleFeature}
              className="mr-2"
            />
            <span>Show in Dashboard</span>
          </label>
        </div>
      </div>

      {/* Today's Saint */}
      {todaySaint && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-2">
                <Church className="w-6 h-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold">Today's Saint</h3>
                {todaySaint.is_feast_day && (
                  <Star className="w-5 h-5 text-yellow-500 ml-2" />
                )}
              </div>
              <p className="text-xl font-bold text-purple-700">{todaySaint.saint_name}</p>
              <p className="text-gray-700 mt-2">{todaySaint.description}</p>
              <p className="text-sm text-gray-500 mt-2">
                {format(new Date(todaySaint.date + 'T12:00:00'), 'MMMM d, yyyy')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handlePreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </h3>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
              {day}
            </div>
          ))}
          
          {/* Empty cells for alignment */}
          {Array.from({ length: monthStart.getDay() }).map((_, i) => (
            <div key={`empty-${i}`} className="p-2" />
          ))}
          
          {/* Calendar days */}
          {days.map(day => {
            const saint = getSaintForDay(day.getDate())
            const isSelected = selectedDate && day.getTime() === selectedDate.getTime()
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => loadSaintForDate(day)}
                className={`
                  relative p-2 border rounded-lg transition-colors
                  ${isToday(day) ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}
                  ${isSelected ? 'bg-purple-100 border-purple-400' : ''}
                  ${saint?.is_feast_day ? 'ring-2 ring-yellow-400' : ''}
                  hover:bg-gray-50
                `}
              >
                <div className="text-sm font-medium">{day.getDate()}</div>
                {saint && (
                  <div className="text-xs text-gray-600 truncate mt-1">
                    {saint.saint_name.split(',')[0]}
                  </div>
                )}
                {saint?.is_feast_day && (
                  <Star className="w-3 h-3 text-yellow-500 absolute top-1 right-1" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Selected Saint Details */}
      {selectedSaint && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {format(new Date(selectedSaint.date + 'T12:00:00'), 'MMMM d, yyyy')}
            </h3>
            {selectedSaint.is_feast_day && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                Feast Day
              </span>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xl font-bold text-purple-700">{selectedSaint.saint_name}</p>
              <p className="text-gray-700 mt-2">{selectedSaint.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Calendar className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Saints This Month</p>
              <p className="text-xl font-bold">{monthSaints?.saints.length || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-yellow-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Feast Days</p>
              <p className="text-xl font-bold">
                {monthSaints?.saints.filter(s => s.is_feast_day).length || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Church className="w-8 h-8 text-purple-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Feature Status</p>
              <p className="text-xl font-bold">{isEnabled ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SaintsCalendar